import { ref } from 'vue';
import { defineStore } from 'pinia';
import { reqGetProfile } from 'src/api/user';
import { reqGetCompanyOcrCredits } from 'src/api/company';

import { usePreferencesStore } from './usePreferencesStore';
import { usePluginsStore } from './usePluginsStore';
import { useWidgetsStore } from './useWidgetsStore';

import { Role } from 'src/constants/app';
import { Subscription, type SubscriptionDetails, type SubscriptionLevel } from 'src/constants/subscription';

export interface ProfileData {
  userId: string;
  username: string;
  email: string;
  name: string;
  surname: string;
  companyName: string;
  companyId: string;
  complianceSubscription: SubscriptionDetails;
  declarativeSubscription: SubscriptionDetails;
  customsbaseSubscription: SubscriptionDetails;
  hasAccessDeltaE: boolean;
  hasAccessGarantyTracking: boolean;
  hasAccessICS2: boolean;
  ocrCreditsRemaining: number;
  ocrCreditsPack: number;
  ocrCreditsUsed: number;
  templatePdfDeltaIE: string | null;
  role: Role;
  acl: string;
  initialized: boolean;
  pictureUrl?: string | null;
}

function createDefaultProfile(): ProfileData {
  return {
    userId: '',
    username: '',
    email: '',
    name: '',
    surname: '',
    companyName: '',
    companyId: '',
    complianceSubscription: Subscription.FREE,
    declarativeSubscription: Subscription.FREE,
    customsbaseSubscription: Subscription.FREE,
    hasAccessDeltaE: false,
    hasAccessGarantyTracking: true,
    hasAccessICS2: false,
    ocrCreditsRemaining: 0,
    ocrCreditsPack: 0,
    ocrCreditsUsed: 0,
    templatePdfDeltaIE: null,
    role: Role.User,
    acl: '',
    initialized: false,
    pictureUrl: null
  };
}

export const useProfileStore = defineStore('profile', () => {
  const userId = ref('');
  const username = ref('');
  const email = ref('');
  const name = ref('');
  const surname = ref('');
  const companyName = ref('');
  const companyId = ref('');
  const complianceSubscription = ref<SubscriptionDetails>(Subscription.FREE);
  const declarativeSubscription = ref<SubscriptionDetails>(Subscription.FREE);
  const customsbaseSubscription = ref<SubscriptionDetails>(Subscription.FREE);
  const hasAccessDeltaE = ref(false);
  const hasAccessGarantyTracking = ref(true);
  const hasAccessICS2 = ref(false);
  const ocrCreditsRemaining = ref(0);
  const ocrCreditsPack = ref(0);
  const ocrCreditsUsed = ref(0);
  const templatePdfDeltaIE = ref<string | null>(null);
  const role = ref<Role>(Role.User);
  const acl = ref('');
  const initialized = ref(false);
  const pictureUrl = ref<string | null>(null);

  function resetProfile() {
    const defaults = createDefaultProfile();
    userId.value = defaults.userId;
    username.value = defaults.username;
    email.value = defaults.email;
    name.value = defaults.name;
    surname.value = defaults.surname;
    companyName.value = defaults.companyName;
    companyId.value = defaults.companyId;
    complianceSubscription.value = defaults.complianceSubscription;
    declarativeSubscription.value = defaults.declarativeSubscription;
    customsbaseSubscription.value = defaults.customsbaseSubscription;
    hasAccessDeltaE.value = defaults.hasAccessDeltaE;
    hasAccessGarantyTracking.value = defaults.hasAccessGarantyTracking;
    hasAccessICS2.value = defaults.hasAccessICS2;
    ocrCreditsRemaining.value = defaults.ocrCreditsRemaining;
    ocrCreditsPack.value = defaults.ocrCreditsPack;
    ocrCreditsUsed.value = defaults.ocrCreditsUsed;
    templatePdfDeltaIE.value = defaults.templatePdfDeltaIE;
    role.value = defaults.role;
    acl.value = defaults.acl;
    initialized.value = defaults.initialized;
    pictureUrl.value = defaults.pictureUrl ?? null;
  }

  function applyOcrCredits(profile: Partial<ProfileData>) {
    ocrCreditsRemaining.value = profile.ocrCreditsRemaining ?? 0;
    ocrCreditsPack.value = profile.ocrCreditsPack ?? 0;
    ocrCreditsUsed.value = profile.ocrCreditsUsed ?? 0;
  }

  async function fetchProfile() {
    try {
      const profile = await reqGetProfile();
      if (profile) {
        userId.value = profile.userId;
        username.value = profile.username;
        email.value = profile.email;
        name.value = profile.name;
        surname.value = profile.surname;
        companyName.value = profile.companyName;
        companyId.value = profile.companyId;
        complianceSubscription.value = Subscription[profile.complianceSubscription as SubscriptionLevel];
        declarativeSubscription.value = Subscription[profile.declarativeSubscription as SubscriptionLevel];
        customsbaseSubscription.value = Subscription[profile.customsbaseSubscription as SubscriptionLevel];
        hasAccessDeltaE.value = profile.hasAccessDeltaE;
        hasAccessGarantyTracking.value = profile.hasAccessGarantyTracking;
        hasAccessICS2.value = profile.hasAccessICS2;
        applyOcrCredits(profile);
        templatePdfDeltaIE.value = profile.templatePdfDeltaIE;
        role.value = profile.role as Role;
        acl.value = profile.acl;
        initialized.value = true;
        pictureUrl.value = profile.pictureUrl || null;
        usePreferencesStore().initPreferences(profile.preferences);
        useWidgetsStore().initWidgets(profile.widgets);
        await usePluginsStore().fetchAndLoadPlugins();
      } else {
        return false;
      }
      return true;
    } catch (error) {
      console.error('Error in fetchProfile:', error);
      return false;
    }
  }

  async function refreshOcrCredits() {
    if (!companyId.value) {
      return false;
    }
    try {
      const credits = await reqGetCompanyOcrCredits(companyId.value);
      if (!credits) return false;
      applyOcrCredits(credits);
      return true;
    } catch (error) {
      console.error('Error in refreshOcrCredits:', error);
      return false;
    }
  }

  return {
    userId,
    username,
    email,
    name,
    surname,
    companyName,
    companyId,
    complianceSubscription,
    declarativeSubscription,
    customsbaseSubscription,
    hasAccessDeltaE,
    hasAccessGarantyTracking,
    hasAccessICS2,
    ocrCreditsRemaining,
    ocrCreditsPack,
    ocrCreditsUsed,
    templatePdfDeltaIE,
    role,
    acl,
    initialized,
    pictureUrl,
    resetProfile,
    fetchProfile,
    refreshOcrCredits
  };
});
